import type { ReactElement, ReactNode, Ref } from "react";
import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { flushSync } from "react-dom";
import * as FormDialog from "./FormDialog";
import { SquareArrowLeftIcon } from "./icons";

type ReplaceDialogProps<Target> = {
  onClose: () => void;
  selectStep: {
    title: TranslationKey;
    continueLabel: TranslationKey;
    fromField: {
      label: TranslationKey;
      value: string;
      icon: ReactNode;
    };
    targetField: {
      label: TranslationKey;
      renderSelect: (props: {
        value: Target | null;
        onChange: (value: Target) => void;
        ref: Ref<unknown>;
        isRequiredError: boolean;
      }) => ReactElement;
    };
  };
  confirmStep: {
    onConfirm: (target: Target) => Promise<unknown>;
    confirmLabel: TranslationKey;
    renderDescription: (props: { target: Target | null }) => ReactNode;
  };
};

type Step = "select" | "confirm";

export const ReplaceDialog = <Target,>({ onClose, selectStep, confirmStep }: ReplaceDialogProps<Target>) => {
  const { t } = useTranslation();
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
  const [step, setStep] = useState<Step>("select");
  const {
    control,
    handleSubmit,
    setFocus,
    reset: resetForm,
  } = useForm<{ target: Target | null }>({ defaultValues: { target: null } });

  const target = useWatch({ control, name: "target" });
  const { mutate, isPending, isError, reset: resetMutation } = useMutation({ mutationFn: confirmStep.onConfirm });

  FormDialog.useAutoFocusField(setFocus, "target");

  const handleClose = () => {
    setStep("select");
    setIsConfirmDisabled(false);
    onClose();
    resetMutation();
    resetForm();
  };

  const goToStep = (nextStep: Step) => {
    flushSync(() => setStep(nextStep));

    if (nextStep === "confirm") {
      /** Disable the confirm button for a short period to prevent confirming on double clicks  */
      setIsConfirmDisabled(true);
      setTimeout(() => setIsConfirmDisabled(false), 250);
    } else {
      setFocus("target");
    }
  };

  return (
    <FormDialog.Dialog isOpen sx={{ width: "34rem", height: "320px" }}>
      <FormDialog.Form
        onSubmit={handleSubmit(({ target }) => {
          if (step === "select") return goToStep("confirm");

          if (isConfirmDisabled) return;
          if (!target) throw new Error("Target is required  when trying to submit the form");

          return mutate(target, { onSuccess: handleClose });
        })}
      >
        {step === "select" ? (
          /** Select step */
          <Stack sx={{ height: "100%" }}>
            <FormDialog.Title>{t(selectStep.title)}</FormDialog.Title>
            <FormDialog.Content>
              <FormDialog.Fields>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    rowGap: 3,
                    columnGap: 5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "600" }}>
                    {t(selectStep.fromField.label)}
                  </Typography>
                  <Stack direction="row" sx={{ px: 2, alignItems: "center", gap: 1 }}>
                    {selectStep.fromField.icon}
                    <Typography variant="body2">{selectStep.fromField.value}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ alignSelf: "flex-start", py: 1, fontWeight: "600" }}>
                    {t(selectStep.targetField.label)}
                  </Typography>
                  <Controller
                    control={control}
                    name="target"
                    rules={{ required: true }}
                    render={({ field: { value, onChange, ref }, fieldState }) => {
                      const isRequiredError = Boolean(fieldState.error);
                      return selectStep.targetField.renderSelect({ value, onChange, ref, isRequiredError });
                    }}
                  />
                </Box>
              </FormDialog.Fields>
            </FormDialog.Content>
            <FormDialog.ActionButtons>
              <FormDialog.CancelButton onClick={handleClose} />
              <FormDialog.SubmitButton>{t(selectStep.continueLabel)}</FormDialog.SubmitButton>
            </FormDialog.ActionButtons>
          </Stack>
        ) : (
          /** Confirm step */
          <Stack sx={{ height: "100%" }}>
            <FormDialog.Title>
              <Box sx={{ transform: "translateX(-6px)" }}>
                <IconButton
                  aria-label={t("GridDrawer.SubDrawer.Back")}
                  onClick={() => goToStep("select")}
                  sx={{ alignSelf: "start", p: 0, border: "none" }}
                >
                  <SquareArrowLeftIcon size={30} />
                </IconButton>
              </Box>
            </FormDialog.Title>
            <FormDialog.Content>
              <FormDialog.FormError isError={isError} />
              <FormDialog.Fields>
                <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14) }}>
                  {confirmStep.renderDescription({ target })}
                </Typography>
              </FormDialog.Fields>
            </FormDialog.Content>
            <FormDialog.ActionButtons>
              <FormDialog.CancelButton onClick={handleClose} />
              <FormDialog.SubmitButton isSubmitting={isPending} isDisabled={isConfirmDisabled}>
                {t(confirmStep.confirmLabel)}
              </FormDialog.SubmitButton>
            </FormDialog.ActionButtons>
          </Stack>
        )}
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
